import React, { useImperativeHandle, forwardRef, useRef } from "react";

const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB per chunk

const LargeFileUploader = forwardRef((props, ref) => {
  const {
    maxSize,
    validExtensions,
    onDrop,
    getResumableUploadUrl,
    onUploadProgress,
    onUploadComplete,
    onUploadError,
    onFileInputError,
    onCompressionStart,
    onCompressionStop,
    onCompressionProgress,
    compressBeforeUpload,
    children,
    className,
  } = props;

  let filesToUploadRef = useRef([]);
  let foldersToUploadRef = useRef([]);
  let uploadingRef = useRef(false);
  let pausedRef = useRef(false);
  const resumeResolverRef = useRef(null);

  // Expose imperative functions including completeUpload.
  useImperativeHandle(ref, () => ({
    startUpload,
    pauseUpload,
    resumeUpload,
    stopUpload,
    completeUpload,
    getFiles,
  }));

  // Check if the file extension is valid.
  const isValidFile = (file) => {
    const extension = file.name.split(".").pop().toLowerCase();
    return validExtensions.includes(extension);
  };

  // Recursively read files from a dropped directory.
  const readDirectory = (directoryEntry, path = "") => {
    return new Promise((resolve) => {
      let files = [];
      const dirReader = directoryEntry.createReader();
      const readEntries = () => {
        dirReader.readEntries(async (entries) => {
          if (entries.length === 0) {
            resolve(files);
          } else {
            const filePromises = entries.map((entry) => {
              if (entry.isFile) {
                return new Promise((res) => {
                  entry.file((file) => {
                    res({ file, relativePath: path + file.name });
                  });
                });
              } else if (entry.isDirectory) {
                return readDirectory(entry, path + entry.name + "/");
              }
            });
            const results = await Promise.all(filePromises);
            results.forEach((result) => {
              if (Array.isArray(result)) {
                files = files.concat(result);
              } else if (result) {
                files.push(result);
              }
            });
            readEntries();
          }
        });
      };
      readEntries();
    });
  };

  const processDrop = async (event) => {
    event.preventDefault();
    const dt = event.dataTransfer;
    const items = dt.items;
    let files = [];
    let folders = [];

    if (items && items.length > 0) {
      const filePromises = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const entry = item.webkitGetAsEntry && item.webkitGetAsEntry();
        if (entry) {
          if (entry.isFile) {
            const file = item.getAsFile();
            if (file) {
              filePromises.push(
                Promise.resolve({ file, relativePath: file.name })
              );
            }
          } else if (entry.isDirectory) {
            filePromises.push(
              readDirectory(entry).then((filesInFolder) => {
                folders.push({
                  folderName: entry.name,
                  files: filesInFolder,
                });
                return null;
              })
            );
          }
        } else {
          const file = item.getAsFile();
          if (file) {
            filePromises.push(
              Promise.resolve({ file, relativePath: file.name })
            );
          }
        }
      }
      const results = await Promise.all(filePromises);
      // For files, filter out any nulls.
      files = results.filter((f) => f !== null);
    } else {
      files = Array.from(event.dataTransfer.files).map((file) => ({
        file,
        relativePath: file.name,
      }));
    }

    // Validate individual files.
    const validFiles = [];
    files.forEach((item) => {
      const { file } = item;
      if (file.name === ".DS_Store") return; // silently ignore .DS_Store
      if (isValidFile(file)) {
        validFiles.push(item);
      } else {
        onFileInputError &&
          onFileInputError(`Invalid file extension: ${file.name}`);
      }
    });

    // Validate files inside folders.
    folders.forEach((folder) => {
      folder.files = folder.files.filter((item) => {
        const { file } = item;
        if (file.name === ".DS_Store") return false;
        if (!isValidFile(file)) {
          onFileInputError &&
            onFileInputError(
              `Invalid file extension in folder ${folder.folderName}: ${file.name}`
            );
          return false;
        }
        return true;
      });
      folder.totalSize = folder.files.reduce(
        (sum, item) => sum + item.file.size,
        0
      );
    });

    // Remove any folder that has no valid files.
    folders = folders.filter((folder) => folder.files.length > 0);

    // Calculate the aggregated size from valid files and folders.
    const validTotalSize =
      validFiles.reduce((acc, item) => acc + item.file.size, 0) +
      folders.reduce((acc, folder) => acc + folder.totalSize, 0);

    // If there are no valid files or folders, do not call onDrop.
    if (validFiles.length === 0 && folders.length === 0) {
      return;
    }

    onDrop && onDrop({ totalSize: validTotalSize, files: validFiles, folders });

    // Check aggregated size against maxSize.
    if (validTotalSize > maxSize) {
      onFileInputError &&
        onFileInputError(
          `Total file size (${validTotalSize} bytes) exceeds the maximum allowed size of ${maxSize} bytes.`
        );
      return;
    }

    filesToUploadRef.current = [...filesToUploadRef.current, ...validFiles];
    foldersToUploadRef.current = [...foldersToUploadRef.current, ...folders];
  };

  // Handle file selection from an input element.
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = [];
    files.forEach((file) => {
      if (file.name === ".DS_Store") return; // silently ignore .DS_Store
      if (isValidFile(file)) {
        validFiles.push(file);
      } else {
        onFileInputError &&
          onFileInputError(`Invalid file extension: ${file.name}`);
      }
    });

    // If no files are valid, do not call onDrop.
    if (validFiles.length === 0) {
      return;
    }

    const validTotalSize = validFiles.reduce((acc, f) => acc + f.size, 0);
    onDrop &&
      onDrop({ totalSize: validTotalSize, files: validFiles, folders: [] });

    if (validTotalSize > maxSize) {
      onFileInputError &&
        onFileInputError(
          `Total file size (${validTotalSize} bytes) exceeds the maximum allowed size of ${maxSize} bytes.`
        );
      return;
    }

    filesToUploadRef.current = validFiles;
    foldersToUploadRef.current = []; // no folders when using file input
  };

  // Compression function using a Web Worker.
  const compressFilesInWorker = (files, folders) => {
    return new Promise((resolve, reject) => {
      const worker = new Worker("compressor.worker.js");
      worker.postMessage({ files, folders });
      onCompressionStart && onCompressionStart();

      worker.onerror = (error) => {
        console.error("Worker error:", error);
        reject(new Error(error.message));
        worker.terminate();
      };

      worker.onmessage = (e) => {
        if (e.data.progress !== undefined) {
          onCompressionProgress && onCompressionProgress(e.data.progress);
        }
        if (e.data.completed) {
          onCompressionStop && onCompressionStop();
          resolve(e.data.blob);
          worker.terminate();
        }
        if (e.data.error) {
          reject(new Error(e.data.error));
          worker.terminate();
        }
      };
    });
  };

  /**
   * Public method: startUpload.
   */
  const startUpload = async () => {
    pausedRef.current = false;
    uploadingRef.current = true;

    // If enabled, compress all files and folders into one zip file.
    if (compressBeforeUpload) {
      try {
        const compressedBlob = await compressFilesInWorker(
          filesToUploadRef.current,
          foldersToUploadRef.current
        );
        // Create a File object from the blob.
        const compressedFile = new File([compressedBlob], "compressed.zip", {
          type: "application/zip",
        });

        // Proceed with uploading the compressed file.
        await uploadFile(compressedFile);
      } catch (err) {
        onUploadError && onUploadError("Compression failed: " + err.message);
        return;
      }
    } else {
      // No compression, proceed with uploading files.
      for (let file of filesToUploadRef.current) {
        if (!uploadingRef.current) {
          console.log("Upload terminated, skipping remaining files.");
          return;
        }
        await uploadFile(file);
      }
    }
  };

  const uploadFile = async (file) => {
    try {
      const resumableUrl = await getResumableUploadUrl(file);
      if (!resumableUrl) {
        onUploadError && onUploadError("Resumable upload URL not provided.");
        return;
      }
      uploadFileInChunks(resumableUrl, file);
      return;
    } catch (error) {
      onUploadError && onUploadError(error.message);
      return;
    }
  };

  const getFiles = () => {
    const standaloneFiles = (filesToUploadRef.current || []).map(
      (item) => item.file || item
    );
    const folderFiles = (foldersToUploadRef.current || []).flatMap((folder) =>
      folder.files.map((item) => item.file || item)
    );
    return [...standaloneFiles, ...folderFiles];
  };

  // The external function that forces upload completion.
  const completeUpload = () => {
    console.log("Force completing upload via completeUpload()");
    // Stop further processing by setting uploadingRef.current to false.
    uploadingRef.current = false;
    onUploadComplete && onUploadComplete();
  };

  // Pause, resume, and stop methods.
  const pauseUpload = () => {
    pausedRef.current = true;
  };

  const resumeUpload = () => {
    pausedRef.current = false;
    if (resumeResolverRef.current) {
      resumeResolverRef.current();
      resumeResolverRef.current = null;
    }
  };

  const stopUpload = () => {
    pausedRef.current = false;
    uploadingRef.current = false;
    if (resumeResolverRef.current) {
      resumeResolverRef.current();
      resumeResolverRef.current = null;
    }
  };

  // Wait until resume is called.
  const waitForResume = () => {
    return new Promise((resolve) => {
      if (!pausedRef.current) {
        resolve();
      } else {
        resumeResolverRef.current = resolve;
      }
    });
  };

  // Upload a file in chunks using the resumable upload URL.
  const uploadFileInChunks = async (resumableUrl, file) => {
    let offset = 0;
    const totalSize = file.size;

    while (offset < totalSize && uploadingRef.current) {
      if (pausedRef.current) await waitForResume();

      // If completeUpload has been called, exit immediately.
      if (!uploadingRef.current) {
        console.log("Upload terminated, skipping further chunks.");
        return;
      }

      const chunk = file.slice(offset, offset + CHUNK_SIZE);
      const chunkSize = chunk.size;
      const end = offset + chunkSize - 1;
      const rangeHeader = `bytes ${offset}-${end}/${totalSize}`;

      let attempts = 0;
      let success = false;

      // Retry mechanism for each chunk.
      while (attempts < 3 && !success) {
        // If upload is terminated, exit immediately.
        if (!uploadingRef.current) {
          console.log("Upload terminated, breaking out of retry loop.");
          return;
        }
        try {
          const response = await fetch(resumableUrl, {
            method: "PUT",
            headers: {
              "Content-Type": file.type,
              "Content-Range": rangeHeader,
            },
            body: chunk,
          });

          // We expect status 308 (Resume Incomplete) or 200/201 when complete.
          if ([200, 201].includes(response.status) || response.status === 308) {
            success = true;
            offset += chunkSize;
            onUploadProgress &&
              onUploadProgress(Math.min(100, (offset / totalSize) * 100));
          } else {
            throw new Error(
              `Chunk upload failed with status ${response.status}`
            );
          }
        } catch (e) {
          attempts++;
          // Check if upload was terminated during retries.
          if (!uploadingRef.current) {
            console.log("Upload terminated during retry, exiting.");
            return;
          }
          if (attempts >= 3) {
            onUploadError &&
              onUploadError(
                `Chunk upload failed for ${file.name} after 3 attempts: ${e.message}`
              );
            pausedRef.current = true;
            await waitForResume();
            attempts = 0;
          } else {
            // Wait a bit before retrying.
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }
        }
      }
    }
    onUploadComplete && onUploadComplete();
  };

  return (
    <div
      className={className}
      onDragOver={(e) => e.preventDefault()}
      onDrop={processDrop}
    >
      <input
        type="file"
        multiple
        webkitdirectory="true"
        onChange={handleFileSelect}
        style={{ display: "none" }}
        id="fileInput"
      />
      <label htmlFor="fileInput" style={{ cursor: "pointer" }}>
        {children}
      </label>
    </div>
  );
});

export default LargeFileUploader;
